<template>
  <DashboardCard col-span="1">
    <template #title>
      <WdsStack gap-sm class="tw-items-center">
        {{ $t('Upcoming actions') }}
        <WdsBadge v-if="actionsCount > 0" :count="actionsCount" md></WdsBadge>
      </WdsStack>
    </template>
    <WdsStack direction-column class="tw-pb-4">
      <!-- Show Spinner While Loading -->
      <div v-if="loading">
        <LoadingList :count="2" avatar />
      </div>

      <!-- Show No Results Message -->
      <WdsStack
        v-else-if="actionsCount === 0"
        align-center
        direction-column
        justify-center
        class="tw-py-4"
      >
        <WdsIcon name="checkmark-outline" color="#757585" :size="18" />
        <WdsText secondary class="tw-mt-1">{{ $t('No new tasks!') }}</WdsText>
        <WdsText secondary>{{ $t('New items will appear here') }}</WdsText>
      </WdsStack>

      <!-- Payment Requests -->
      <WdsStack v-else direction-column gap-sm>
        <UpcomingActionsRow
          :title="$t('Payment requests')"
          :show="pendingPayments > 0"
          :icon="WdsIconNameEnum.RECEIPT"
          :action-text="$t('Review')"
          :route="`${laravelNamedRoute('billing.fmsBills')}?bill-status=UNAPPROVED`"
        >
          <template #default>
            <WdsText primary>
              <div class="tw-text-indigo-500">{{ pendingPayments }} {{ $t('pending') }}</div>
            </WdsText>
          </template>
        </UpcomingActionsRow>

        <!-- Invoices -->
        <UpcomingActionsRow
          :title="$t('Invoices \& payments')"
          :show="dueInvoices + overdueInvoices > 0"
          :icon="WdsIconNameEnum.CURRENCY"
          :action-text="$t('Pay')"
          :route="`${laravelNamedRoute('billing.invoices.index')}?invoice-status=UNPAID`"
        >
          <template #default>
            <WdsStack gap-xs>
              <WdsText>
                <span class="tw-text-indigo-500">{{ dueInvoices }} {{ $t('invoices due') }}</span>
                /
              </WdsText>
              <WdsText>
                <span :class="overdueInvoices > 0 ? 'tw-text-orange-600' : ''">
                  {{ overdueInvoices }} {{ $t('overdue') }}
                </span>
              </WdsText>
            </WdsStack>
          </template>
        </UpcomingActionsRow>

        <!-- Approval Requests -->
        <UpcomingActionsRow
          :title="$t('Approval workflows')"
          :show="approvalRequests > 0"
          :icon="WdsIconNameEnum.STAMP"
          :action-text="$t('Review')"
          :route="laravelNamedRoute('approval.index')"
        >
          <template #default>
            <WdsText>
              <div class="tw-text-indigo-500">
                {{ approvalRequests }} {{ $t('approval requests') }}
              </div>
            </WdsText>
          </template>
        </UpcomingActionsRow>

        <UpcomingActionsRow
          v-if="false"
          :title="$t('Job bids')"
          :show="unansweredBids > 0"
          :icon="WdsIconNameEnum.BID"
          :action-text="$t('Review')"
          route="/jobs/created?hasBids=dHJ1ZQ=="
        >
          <template #default>
            <WdsText>
              <div class="tw-text-indigo-500">
                {{ $tc(':count unanswered bid | :count unanswered bids', unansweredBids) }}
              </div>
            </WdsText>
          </template>
        </UpcomingActionsRow>

        <UpcomingActionsRow
          :title="$t('Contracts')"
          :show="hiresMissingContractType > 0"
          :icon="WdsIconNameEnum.MANAGE_PROTECTION"
          :action-text="$t('Review')"
          route="/profiles/contracts?table=hires&&contractType=W3siX190eXBlbmFtZSI6Il9fRW51bVZhbHVlIiwibmFtZSI6IkNPTlRSQUNUX1RZUEVfVU5LTk9XTiIsImRlc2NyaXB0aW9uIjoiVW5rbm93biIsImlzRGVwcmVjYXRlZCI6ZmFsc2UsImRlcHJlY2F0aW9uUmVhc29uIjpudWxsLCJsYWJlbCI6IlVua25vd24iLCJ2YWx1ZSI6IkNPTlRSQUNUX1RZUEVfVU5LTk9XTiJ9LHsiX190eXBlbmFtZSI6Il9fRW51bVZhbHVlIiwibmFtZSI6IkNPTlRSQUNUX1RZUEVfVU5ERVRFUk1JTkVEIiwiZGVzY3JpcHRpb24iOiJVbmRldGVybWluZWQiLCJpc0RlcHJlY2F0ZWQiOmZhbHNlLCJkZXByZWNhdGlvblJlYXNvbiI6bnVsbCwibGFiZWwiOiJVbmRldGVybWluZWQiLCJ2YWx1ZSI6IkNPTlRSQUNUX1RZUEVfVU5ERVRFUk1JTkVEIn1d"
        >
          <template #default>
            <WdsText>
              <div class="tw-text-indigo-500">
                {{
                  $tc(
                    ':count hire missing contract type | :count hires missing contract type',
                    hiresMissingContractType,
                  )
                }}
              </div>
            </WdsText>
          </template>
        </UpcomingActionsRow>
      </WdsStack>
    </WdsStack>
  </DashboardCard>
</template>

<script setup lang="ts">
import { WdsBadge, WdsIconNameEnum, WdsStack, WdsText, WdsIcon } from '@worksome/wds-components'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useRouter } from 'vue-router'
import { graphql } from '@/gql'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import UpcomingActionsRow from '@/components/Dashboard/Widgets/UpcomingActions/UpcomingActionsRow.vue'
import { InvoiceStatus, PaymentRequestStatus } from '@/gql/graphql'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'

const { $t, $tc } = useTranslation()
const router = useRouter()

const { result, loading } = useQuery(
  graphql(`
    query UpcomingActions(
      $accounts: [ID!]
      $users: [ID!]
      $paymentRequestStatuses: [PaymentRequestStatus!]
      $invoiceStatuses: [InvoiceStatus!]
    ) {
      paymentRequests(accounts: $accounts, statuses: $paymentRequestStatuses, first: 10000) {
        data {
          id
        }
      }
      invoices(accounts: $accounts, status: $invoiceStatuses, first: 10000) {
        data {
          id
          isOverDue
        }
      }
      approvalApprovables(
        accounts: $accounts
        requiresAction: true
        requiresActionUsers: $users
        first: 10000
      ) {
        data {
          id
        }
      }
      bids(accounts: $accounts, statuses: [OPEN]) {
        data {
          id
        }
      }
      hires(
        accounts: $accounts
        contractType: [CONTRACT_TYPE_UNDETERMINED, CONTRACT_TYPE_UNKNOWN]
      ) {
        data {
          id
        }
      }
    }
  `),
  {
    accounts: [currentAccount.globalId],
    users: [currentUser.globalId],
    paymentRequestStatuses: [PaymentRequestStatus.Unapproved],
    invoiceStatuses: [InvoiceStatus.Unpaid],
  },
  { clientId: 'publicApi' },
)

const pendingPayments = computed(() => result.value?.paymentRequests?.data.length || 0)
const dueInvoices = computed(() => result.value?.invoices?.data.length || 0)
const overdueInvoices = computed(
  () => (result.value?.invoices?.data ?? []).filter((invoice) => invoice.isOverDue).length,
)
const approvalRequests = computed(() => result.value?.approvalApprovables?.data.length || 0)

const unansweredBids = computed(() => result.value?.bids.data.length || 0)

const hiresMissingContractType = computed(() => result.value?.hires.data.length ?? 0)

const actionsCount = computed(
  () =>
    pendingPayments.value +
    dueInvoices.value +
    overdueInvoices.value +
    approvalRequests.value +
    hiresMissingContractType.value,
)
</script>

<style lang="scss" scoped></style>
